/**
 * MissingKeyReporterContext
 * 
 * READ-ONLY missing key detection and logging system.
 * 
 * This context:
 * 1. Detects missing translation keys at runtime (fallback to defaultValue)
 * 2. Batches and deduplicates reports
 * 3. Logs missing keys for dev tooling visibility
 * 
 * NOTE: Self-healing (AI translation) is now delegated to GitHub Actions workflow.
 * The self-heal-localization edge function is no longer invoked from browser.
 */

import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { isKeysMode } from '@/i18n/config';
import { logger } from '@/utils/logger';

interface MissingKeyReport {
  namespace: string;
  key: string;
  defaultValue: string;
  route: string;
}

interface MissingKeyReporterContextType {
  /** Report a missing key detected at runtime */
  reportMissingKey: (namespace: string, key: string, defaultValue: string) => void;
  /** Map of translated keys - always empty (self-healing delegated to GitHub Actions) */
  translations: Map<string, string>;
  /** Whether healing is in progress - always false (execution delegated) */
  isHealing: boolean;
  /** Number of keys pending in queue */
  pendingCount: number;
}
// ============ Constants ============

const DEBOUNCE_MS = 2000; // 2 seconds
const MAX_BATCH_SIZE = 20; // Max keys per log batch
const RATE_LIMIT_MS = 10000; // 10 seconds between batches
const MAX_DEFAULT_VALUE_LENGTH = 2000; // Prevent injection of huge strings
const SESSION_ID = `sh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// ============ Context ============

const MissingKeyReporterContext = createContext<MissingKeyReporterContextType | undefined>(undefined);

// ============ Provider ============

export const MissingKeyReporterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { settings } = useLocale();
  
  // Queue of pending reports
  const queueRef = useRef<Map<string, MissingKeyReport>>(new Map());
  
  // Set of keys already reported this session (deduplication)
  const reportedKeysRef = useRef<Set<string>>(new Set());
  
  // Debounce timer
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Rate limit tracking
  const lastBatchTimeRef = useRef<number>(0);
  
  // State for UI
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());
  const [isHealing, setIsHealing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Process the queue - LOG ONLY (execution delegated to scheduled GitHub Action)
  // The self-heal-localization edge function is no longer invoked from browser
  // Keys are stored locally and logged for debugging; actual healing happens via cron
  const processQueue = useCallback(async () => {
    const queue = queueRef.current;
    if (queue.size === 0) return;

    // Rate limiting
    const now = Date.now();
    const timeSinceLastBatch = now - lastBatchTimeRef.current;
    if (timeSinceLastBatch < RATE_LIMIT_MS && lastBatchTimeRef.current > 0) {
      // Schedule retry after rate limit expires
      const waitTime = RATE_LIMIT_MS - timeSinceLastBatch;
      logger.debug(`Self-heal rate limited, waiting ${waitTime}ms`);
      timerRef.current = setTimeout(processQueue, waitTime);
      return;
    }

    // Take up to MAX_BATCH_SIZE items
    const keysToProcess: MissingKeyReport[] = [];
    for (const [key, report] of queue.entries()) {
      if (keysToProcess.length >= MAX_BATCH_SIZE) break;
      keysToProcess.push(report);
      queue.delete(key);
    }

    if (keysToProcess.length === 0) return;

    // Update pending count
    setPendingCount(queue.size);
    lastBatchTimeRef.current = now;

    // LOG ONLY - No edge function invocation
    // Self-healing is now handled by scheduled GitHub Action workflow
    logger.info(`[Self-Heal] Detected ${keysToProcess.length} missing keys for locale ${settings.locale}`, {
      keys: keysToProcess.map(k => `${k.namespace}.${k.key}`),
      locale: settings.locale,
      sessionId: SESSION_ID,
    });

    // Store in localStorage for dev tooling visibility
    try {
      const stored = JSON.parse(localStorage.getItem('missing_localization_keys') || '[]');
      const newKeys = keysToProcess.map(k => ({
        namespace: k.namespace,
        key: k.key,
        defaultValue: k.defaultValue,
        route: k.route,
        locale: settings.locale,
        timestamp: new Date().toISOString(),
      }));
      localStorage.setItem('missing_localization_keys', JSON.stringify([...stored, ...newKeys].slice(-100)));
    } catch {
      // localStorage might be full or unavailable
    }

    // If there are more items in queue, schedule next batch
    if (queue.size > 0) {
      timerRef.current = setTimeout(processQueue, DEBOUNCE_MS);
    }
  }, [settings.locale]);

  // Report a missing key
  const reportMissingKey = useCallback((namespace: string, key: string, defaultValue: string) => {
    // Skip in edit mode or keys debug mode
    if (isKeysMode()) return;
    
    // Skip if EN locale (source language)
    if (settings.locale === 'en' || settings.locale === 'en-US') return;
    
    // Validate defaultValue
    if (!defaultValue || typeof defaultValue !== 'string') return;
    if (defaultValue.length > MAX_DEFAULT_VALUE_LENGTH) return;
    
    // Deduplication - don't report the same key twice in a session
    const compositeKey = `${namespace}.${key}`;
    if (reportedKeysRef.current.has(compositeKey)) return;
    reportedKeysRef.current.add(compositeKey);
    
    // Add to queue
    queueRef.current.set(compositeKey, {
      namespace,
      key,
      defaultValue: defaultValue.slice(0, MAX_DEFAULT_VALUE_LENGTH), // Truncate for safety
      route: location.pathname,
    });
    
    setPendingCount(queueRef.current.size);
    
    // Debounce - reset timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(processQueue, DEBOUNCE_MS);
    
  }, [location.pathname, settings.locale, processQueue]);

  return (
    <MissingKeyReporterContext.Provider
      value={{
        reportMissingKey,
        translations,
        isHealing,
        pendingCount,
      }}
    >
      {children}
    </MissingKeyReporterContext.Provider>
  );
};

// ============ Hook ============

export const useMissingKeyReporter = (): MissingKeyReporterContextType => {
  const context = useContext(MissingKeyReporterContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    // This allows the hook to be used in components that might render
    // before the provider is mounted
    return {
      reportMissingKey: () => {},
      translations: new Map(),
      isHealing: false,
      pendingCount: 0,
    };
  }
  return context;
};
